import { ChevronDown, ChevronRight, Folder, FolderOpen, Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from "motion/react";
import { Dialog as Primitive } from "radix-ui";
import { Fragment, type ReactNode, useId, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";

import { pestanaVisible, seccionesVisibles } from "@/app/menu-visible";
import { navigation, type NavChild, type NavItem } from "@/app/navigation";
import { useUsuario } from "@/features/acceso/api";
import { WelcomeGate } from "@/features/bienvenida/WelcomeGate";
import { cn } from "@/lib/cn";

import { BarraSuperior } from "./barra/BarraSuperior";
import { BotonMejoremos } from "@/features/mejoras/BotonMejoremos";

/** Los grupos del menú principal, en su orden. Hoy solo hay uno; el resto va suelto arriba. */
const GRUPOS_DEL_MENU = ["Opciones generales"];

/**
 * Armazón de la aplicación.
 *
 * Escritorio: riel de íconos, barra lateral con el árbol de secciones y sus carpetas, y
 * migas de pan arriba. Celular: barra inferior con botones de 44 px o más, al alcance del
 * pulgar en planta.
 */
/** Si la barra lateral quedó contraída la última vez, se abre así. */
const COLLAPSED_KEY = "neca.sidebar.collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell() {
  const location = useLocation();
  const usuario = useUsuario();
  // Qué secciones y pestañas ve cada quien: la regla vive en `app/menu-visible` y la comparte el
  // buscador de la barra (Ctrl+K), para que los dos ofrezcan las mismas puertas.
  const menu = seccionesVisibles(usuario.data);
  const verPestana = pestanaVisible(usuario.data);
  const reduceMotion = useReducedMotion();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  /**
   * El cajón del celular: abierto o no. Se cierra al elegir un enlace y, si la dirección cambia por
   * otro camino (atrás del navegador), se borra aquí mismo: guardarlo y solo compararlo hacía que
   * reapareciera abierto al volver a la pantalla donde se abrió.
   */
  const [cajon, setCajon] = useState<{ abierto: boolean; en: string }>({ abierto: false, en: location.pathname });
  if (cajon.abierto && cajon.en !== location.pathname) setCajon({ abierto: false, en: location.pathname });

  const toggleSidebar = () => {
    setCollapsed((open) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, open ? "0" : "1");
      } catch {
        // Sin almacenamiento la barra simplemente no recuerda el estado.
      }
      return !open;
    });
  };
  // Se anima al cambiar de sección (/costos, /sistema…), no al cambiar de pestaña dentro de
  // ella: así el encabezado y las pestañas no parpadean.
  const section = location.pathname.split("/")[1] ?? "";

  return (
    <div className="flex min-h-dvh flex-col op-shell">
      <WelcomeGate />
      <BotonMejoremos />
      {/* La barra blanca va de lado a lado, encima del menú (owner 16-oct): el logo queda en la
          esquina y no hay un bloque negro vacío arriba a la izquierda, abierto o contraído. */}
      {/**
        * **La barra superior blanca** (#30), en escritorio y en el celular. La cuenta va en ella y no
        * al pie del menú: el menú se contrae y entonces salir de la sesión desaparecería con él.
        *
        * **En el celular, el árbol completo va en un cajón** que se abre desde la barra. La barra de
        * abajo lleva solo las secciones; las carpetas —las cinco líneas de Producción, las pestañas de
        * Comercial y de Costos— vivían únicamente en la barra lateral, que en el celular no existe. El
        * cajón es el mismo árbol de escritorio (`MenuTree`), y se cierra solo al elegir un enlace o al
        * cambiar de pantalla.
        */}
      <BarraSuperior
        ubicacion={<Breadcrumbs pathname={location.pathname} />}
        menuMovil={
          <div className="lg:hidden">
            <Primitive.Root open={cajon.abierto} onOpenChange={(abierto) => setCajon({ abierto, en: location.pathname })}>
              <Primitive.Trigger
                aria-label="Ver todas las secciones"
                className="-ml-2 grid size-11 place-items-center rounded-lg text-light-2 transition-colors hover:bg-white/10 hover:text-light"
              >
                <Menu aria-hidden className="size-5" />
              </Primitive.Trigger>
              <Primitive.Portal>
                <Primitive.Overlay className="fixed inset-0 z-30 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <Primitive.Content
                  onClick={(event) => {
                    if ((event.target as Element).closest("a")) setCajon({ abierto: false, en: location.pathname });
                  }}
                  className="rail fixed inset-y-0 left-0 z-40 flex w-[min(20rem,86vw)] flex-col py-4 data-[state=open]:animate-in data-[state=open]:slide-in-from-left"
                >
                  <div className="flex items-center justify-between gap-2 px-6 pb-4">
                    <img src="/logo.svg" alt="NECA" className="h-5 w-auto invert" />
                    <Primitive.Title className="sr-only">Todas las secciones</Primitive.Title>
                    <Primitive.Description className="sr-only">El menú completo del ERP, con sus carpetas</Primitive.Description>
                    <Primitive.Close
                      aria-label="Cerrar el menú"
                      className="-mr-2 grid size-11 place-items-center rounded-lg text-light-3 transition-colors hover:bg-white/10 hover:text-light"
                    >
                      <X aria-hidden className="size-5" />
                    </Primitive.Close>
                  </div>
                  <MenuTree label="Todas las secciones" items={menu} pathname={location.pathname} can={verPestana} />
                </Primitive.Content>
              </Primitive.Portal>
            </Primitive.Root>
          </div>
        }
      />
      <div className={cn("flex-1 lg:grid", collapsed ? "lg:grid-cols-[76px_1fr]" : "lg:grid-cols-[268px_1fr]")}>
      {/**
        * **El riel de íconos solo existe con el menú contraído.**
        *
        * Con el menú abierto eran dos menús, uno al lado del otro, diciendo lo mismo: la columna de
        * íconos y el árbol con esos mismos íconos y su nombre. 76 píxeles de ancho gastados en repetir,
        * en una pantalla donde la rejilla de la semana pide todo el ancho que haya.
        *
        * Contraído sí hace falta: es lo único que queda para moverse y para volver a abrirlo.
        */}
      {collapsed ? <IconRail items={menu} collapsed={collapsed} onOpen={toggleSidebar} /> : null}

      <aside className={cn("rail hidden lg:sticky lg:top-0 lg:h-[calc(100dvh-57px)] lg:flex-col lg:border-r lg:border-line-dark lg:py-6", !collapsed && "lg:flex")}>
        {/* El logo vive en la barra superior (#30); contraer el menú va junto a «Secciones» (16-oct). */}
        <MenuTree
          label="Principal"
          items={menu}
          pathname={location.pathname}
          can={verPestana}
          accion={
            <button
              type="button"
              onClick={toggleSidebar}
              title="Contraer el menú"
              aria-label="Contraer el menú"
              aria-expanded="true"
              className="-my-2 grid size-9 place-items-center rounded-lg text-light-3 transition-colors hover:bg-white/10 hover:text-light"
            >
              <PanelLeftClose aria-hidden className="size-4.5" />
            </button>
          }
        />
      </aside>

      <div className="flex min-w-0 flex-col">

        <main className="flex-1 px-4 pt-4 pb-28 sm:px-6 lg:px-10 lg:pt-8 lg:pb-12">
          {/* LazyMotion con domAnimation carga solo las animaciones que se usan: menos peso en 4G. */}
          <LazyMotion features={domAnimation} strict>
            <AnimatePresence mode="wait" initial={false}>
              <m.div
                key={section}
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Outlet />
              </m.div>
            </AnimatePresence>
          </LazyMotion>
        </main>

        <nav
          aria-label="Principal"
          className="rail fixed inset-x-0 bottom-0 z-10 grid auto-cols-fr grid-flow-col border-t border-line-dark pb-[env(safe-area-inset-bottom)] lg:hidden"
        >
          {menu.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex min-h-14 flex-col items-center justify-center gap-1 text-xs font-medium text-light-3",
                  isActive && "text-light",
                )
              }
            >
              <Icon aria-hidden className="size-5" strokeWidth={1.6} />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
      </div>
    </div>
  );
}

/**
 * El árbol de secciones con sus carpetas: la barra lateral de escritorio y el cajón del celular.
 *
 * **Dos bloques: el trabajo y las herramientas.** Las primeras son el camino de un pedido —se
 * cotiza, se vende, se fabrica— y las de abajo son a dónde se va a consultar o configurar.
 * Seguidas, había que leerlas todas para encontrar una; partidas, se mira en el bloque que toca.
 */
function MenuTree({ label, items, pathname, can, accion }: {
  label: string; items: NavItem[]; pathname: string; can: (child: NavChild) => boolean;
  /** Lo que va a la derecha de «Secciones»: en escritorio, contraer el menú. */
  accion?: ReactNode;
}) {
  return (
    <nav aria-label={label} className="grid grid-cols-[minmax(0,1fr)] content-start gap-1 overflow-x-hidden overflow-y-auto px-4">
      <div className="flex items-center justify-between gap-2 px-3 pb-2">
        <p className="text-[10px] font-semibold tracking-[0.2em] text-light-3 uppercase">Secciones</p>
        {accion}
      </div>
      {items
        .filter((item) => !item.group)
        .map((item) => (
          <TreeItem key={item.to} item={item} pathname={pathname} can={can} />
        ))}
      {GRUPOS_DEL_MENU.map((grupo) => {
        const dentro = items.filter((item) => item.group === grupo);
        // Un título sobre una lista vacía promete algo que no hay: si nadie del grupo pasa el
        // filtro de permisos, el grupo entero desaparece.
        if (dentro.length === 0) return null;
        return (
          <div key={grupo} className="mt-5 grid grid-cols-[minmax(0,1fr)] gap-1">
            <p className="px-3 pb-1 text-[10px] font-semibold tracking-[0.2em] text-light-3 uppercase">{grupo}</p>
            {dentro.map((item) => (
              <TreeItem key={item.to} item={item} pathname={pathname} can={can} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}

/** Columna angosta con abrir el menú y un ícono por sección (la marca está en la barra). */
function IconRail({ items, collapsed, onOpen }: { items: NavItem[]; collapsed: boolean; onOpen: () => void }) {
  return (
    <div className="rail hidden lg:sticky lg:top-0 lg:flex lg:h-[calc(100dvh-57px)] lg:flex-col lg:items-center lg:gap-3 lg:border-r lg:border-line-dark lg:py-6">
      {collapsed ? (
        <button
          type="button"
          onClick={onOpen}
          title="Abrir el menú"
          aria-label="Abrir el menú"
          aria-expanded="false"
          className="mb-1 grid size-9 place-items-center rounded-lg text-light-3 transition-colors hover:bg-white/10 hover:text-light"
        >
          <PanelLeftOpen aria-hidden className="size-4.5" />
        </button>
      ) : (
        <span className="mb-1 block h-9" />
      )}
      <nav aria-label="Secciones" className="grid gap-3">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            title={label}
            aria-label={label}
            className={({ isActive }) =>
              cn(
                "grid size-11 place-items-center rounded-xl text-light-2 transition-colors hover:text-light",
                isActive ? "glass-active text-light" : "glass",
              )
            }
          >
            <Icon aria-hidden className="size-5" strokeWidth={1.6} />
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Sección del menú; si tiene carpetas y está abierta, las muestra con líneas de árbol.
 *
 * **La flecha se puede tocar, y antes no.** Era un dibujo dentro del enlace: el árbol se abría
 * solo según la dirección, así que estando dentro de una sección quedaba abierto para siempre y
 * la flecha no hacía nada. Con las carpetas de Producción, eso empuja el resto del menú
 * fuera de la pantalla y no había forma de recogerlo.
 *
 * Ahora es un botón de verdad, **hermano del enlace y no hijo**: un botón dentro de un enlace no
 * es HTML válido, y en la práctica significa que con el teclado no se puede llegar a uno de los
 * dos. Lleva `aria-expanded` y dice qué hace, para que quien navegue con lector sepa que eso se
 * abre y se cierra.
 *
 * Por omisión se abre al entrar en la sección —que es lo que hacía y estaba bien— y lo que se
 * añade es poder cerrarla a mano. La decisión de quien mira **gana sobre la automática** y se
 * queda: si alguien recoge Producción, sigue recogida al moverse dentro de ella. Volver a
 * abrirla es un clic, y reabrirla sola sería discutirle a alguien que ya dijo lo que quiere.
 */
function TreeItem({ item, pathname, can }: { item: NavItem; pathname: string; can: (child: NavChild) => boolean }) {
  const { to, label, icon: Icon } = item;
  // Las carpetas también se filtran: «Mano de obra» no se enseña a quien no ve la nómina, ni una
  // línea de producción a quien su cargo no alcanza.
  const children = item.children?.filter(can);
  /** Lo que ha decidido quien mira. `null` mientras no ha tocado la flecha. */
  const [aMano, setAMano] = useState<boolean | null>(null);
  const dentro = Boolean(children) && pathname.startsWith(to);
  const open = Boolean(children) && (aMano ?? dentro);
  const Chevron = open ? ChevronDown : ChevronRight;
  // Un id por instancia: el mismo árbol vive en la barra lateral y en el cajón del celular.
  const listaId = useId();

  return (
    <div>
      <div className="flex items-center gap-1">
        <NavLink
          to={to}
          end={to === "/"}
          className={({ isActive }) =>
            cn(
              "flex min-h-11 flex-1 items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-light-2 transition-colors hover:text-light",
              isActive ? "glass-active text-light" : "hover:bg-white/5",
            )
          }
        >
          <Icon aria-hidden className="size-[18px]" strokeWidth={1.6} />
          <span className="flex-1">{label}</span>
          {item.Contador ? <item.Contador /> : null}
        </NavLink>

        {children ? (
          <button
            type="button"
            onClick={() => setAMano(!open)}
            aria-expanded={open}
            aria-controls={listaId}
            aria-label={`${open ? "Recoger" : "Desplegar"} ${label}`}
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-light-3 transition-colors hover:bg-white/5 hover:text-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-light"
          >
            <Chevron aria-hidden className="size-4" />
          </button>
        ) : null}
      </div>

      {open && children ? (
        <ul
          id={listaId}
          className="relative mt-1 mb-2 ml-5 grid grid-cols-[minmax(0,1fr)] gap-0.5 pl-4 before:absolute before:top-0 before:bottom-5 before:left-0 before:w-px before:bg-line"
        >
          {children.map((child, i) => (
            <Fragment key={child.to}>
              {/**
                * **El título del grupo, cuando cambia.**
                *
                * Las entradas de Producción no son la misma clase de cosa: cinco son líneas
                * —dónde se fabrica— y el resto es control de planta —cómo va—. Sin los títulos hay
                * que leerlas una por una, y eso se hace varias veces al día.
                */}
              {child.group && child.group !== children[i - 1]?.group ? (
                <li className="mt-3 px-2.5 pb-0.5 text-[9px] font-semibold tracking-[0.18em] text-light-3 uppercase first:mt-0">
                  {child.group}
                </li>
              ) : null}
              <li className="relative before:absolute before:top-1/2 before:-left-4 before:h-px before:w-3 before:bg-line">
                {child.soon ? (
                  /**
                   * **Lo que no existe se enseña apagado, y sin enlace.**
                   *
                   * Se enseña porque el owner lo pidió —«deja los nombres y todo expresado para ver
                   * dónde lo vamos a ir conectando»— y porque un hueco con su nombre dice qué falta.
                   *
                   * Y **sin enlace**: aquí eran enlaces normales, así que la barra lateral llevaba a
                   * «Color» o a «Parte diario» y lo que salía era la pantalla de «no encontrada». Un
                   * menú que lleva a un callejón gasta el mismo clic que uno que avisa, y además
                   * hace dudar de los demás. Con `aria-disabled` y sin `href`, quien navegue con
                   * teclado o con lector tampoco se estrella.
                   */
                  <span
                    aria-disabled="true"
                    className="flex min-h-10 items-center gap-2.5 rounded-md px-2.5 text-[13px] text-light-3 opacity-55"
                  >
                    <Folder aria-hidden className="size-4 shrink-0" strokeWidth={1.6} />
                    <span className="min-w-0 flex-1 truncate">{child.label}</span>
                    <span className="shrink-0 rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide uppercase">
                      pronto
                    </span>
                  </span>
                ) : (
                  <NavLink
                    to={`${to}/${child.to}`}
                    className={({ isActive }) =>
                      cn(
                        "group flex min-h-10 items-center gap-2.5 rounded-md px-2.5 text-[13px] text-light-3 transition-colors hover:text-light",
                        isActive ? "bg-white/[0.08] text-light" : "hover:bg-white/5",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? (
                          <FolderOpen aria-hidden className="size-4 shrink-0" strokeWidth={1.6} />
                        ) : (
                          <Folder aria-hidden className="size-4 shrink-0" strokeWidth={1.6} />
                        )}
                        <span className="min-w-0 flex-1 truncate">{child.label}</span>
                      </>
                    )}
                  </NavLink>
                )}
              </li>
            </Fragment>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Dónde está la persona: sección y pestaña, con enlace para volver a la sección. */
function Breadcrumbs({ pathname }: { pathname: string }) {
  const item = navigation.find((n) => (n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)));
  const child = item?.children?.filter((c) => pathname.startsWith(`${item.to}/${c.to}`)).sort((a, b) => b.to.length - a.to.length)[0];
  if (!item) return null;
  return (
    <nav aria-label="Ubicación">
      <ol className="flex items-center gap-2 text-sm text-light-3">
        {/* «NECA» como primer escalón ya no va: el logo de la barra, al lado, lleva al inicio. */}
        <li>
          {child ? (
            <Link to={item.to} className="hover:text-light">
              {item.label}
            </Link>
          ) : (
            <span aria-current="page" className="text-light">
              {item.label}
            </span>
          )}
        </li>
        {child ? (
          <>
            <li aria-hidden>/</li>
            <li>
              <span aria-current="page" className="text-light">
                {child.label}
              </span>
            </li>
          </>
        ) : null}
      </ol>
    </nav>
  );
}
